"use client";

import { CalendarCheck, Lock, Star } from "lucide-react";
import type { CSSProperties } from "react";

import { CcDemoTabs } from "@/components/services/shared/CcDemoTabs";
import { CcPhotoScene, CcToolLogo } from "@/components/services/shared/CcPhotoScene";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { WebValueDemoCopy } from "./web-value-demo-copy";

type CcWebValueProps = {
  copy: WebValueDemoCopy;
};

/**
 * "Värdet av en bra hemsida" on the webbyrå page: three tabs, each a real
 * photo of a person with realistic cards for the made-up painting firm laid
 * over it (first impression → speed → enquiries), using real logos.
 * Same section header and tab control as the SEO page's CcSeoValue.
 */
export function CcWebValue({ copy }: CcWebValueProps) {
  return (
    <section
      className="cc-section cc-seo-value"
      aria-labelledby="cc-web-value-title"
      data-cc-nav="light"
      {...devSectionAttr("web-value")}
    >
      <div className="cc-container">
        <header className="cc-seo-value__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-web-value-title" className="cc-seo-heading">
            {copy.title}
          </h2>
          <p className="cc-seo-lead cc-seo-value__lead">{copy.lead}</p>
        </header>

        <CcDemoTabs
          tablistLabel={copy.tablistLabel}
          disclaimer={copy.disclaimer}
          tabs={[
            {
              key: "design",
              label: copy.tabs.design,
              summary: copy.design.summary,
              content: <DesignView copy={copy.design} />,
            },
            {
              key: "speed",
              label: copy.tabs.speed,
              summary: copy.speed.summary,
              content: <SpeedView copy={copy.speed} />,
            },
            {
              key: "contact",
              label: copy.tabs.contact,
              summary: copy.contact.summary,
              content: <ContactView copy={copy.contact} />,
            },
          ]}
        />

        <p className="cc-seo-value__footnote">{copy.footnote}</p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- 1. first impression

function DesignView({ copy }: { copy: WebValueDemoCopy["design"] }) {
  const site = copy.site;
  return (
    <CcPhotoScene photo={copy.photo} variant="design">
      <div
        className="cc-wv-card cc-wv-site"
        style={{ "--d": 0 } as CSSProperties}
      >
        <div className="cc-wv-site__bar">
          <span>
            <i />
            <i />
            <i />
          </span>
          <em>
            <Lock size={10} strokeWidth={2.4} />
            {site.url}
          </em>
        </div>
        <div className="cc-wv-site__body">
          <div className="cc-wv-site__nav">
            <b>{site.brand}</b>
            <span>
              {site.nav.map((item) => (
                <em key={item}>{item}</em>
              ))}
            </span>
            <i>{site.navCta}</i>
          </div>
          <small>{site.eyebrow}</small>
          <strong>{site.heading}</strong>
          <p>{site.text}</p>
          <i className="cc-wv-site__cta">{site.cta}</i>
          <ul>
            {site.services.map((service) => (
              <li key={service}>{service}</li>
            ))}
          </ul>
        </div>
      </div>

      <div
        className="cc-wv-card cc-wv-review"
        style={{ "--d": 1 } as CSSProperties}
      >
        <CcToolLogo name="google" size={22} />
        <span>
          <b>
            {copy.review.value}
            <span className="cc-wv-review__stars">
              {[0, 1, 2, 3, 4].map((star) => (
                <Star
                  key={star}
                  size={13}
                  fill="currentColor"
                  strokeWidth={0}
                />
              ))}
            </span>
          </b>
          <small>{copy.review.label}</small>
        </span>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 2. fast everywhere

const RING = 2 * Math.PI * 17;

function SpeedView({ copy }: { copy: WebValueDemoCopy["speed"] }) {
  const slowest = Math.max(...copy.load.map((row) => row.seconds));
  return (
    <CcPhotoScene photo={copy.photo} variant="speed">
      <div
        className="cc-wv-card cc-wv-scores"
        style={{ "--d": 0 } as CSSProperties}
      >
        <p className="cc-wv-card__head">
          <CcToolLogo name="google" />
          {copy.scoresTitle}
          <em>{copy.scoresMeta}</em>
        </p>
        <ul>
          {copy.scores.map((score) => (
            <li key={score.label}>
              <svg viewBox="0 0 40 40">
                <circle cx="20" cy="20" r="17" className="cc-wv-ring__track" />
                <circle
                  cx="20"
                  cy="20"
                  r="17"
                  className="cc-wv-ring__value"
                  style={
                    {
                      "--len": `${RING}`,
                      "--to": `${RING * (1 - score.value / 100)}`,
                    } as CSSProperties
                  }
                />
                <text x="20" y="24.5">
                  {score.value}
                </text>
              </svg>
              <span>{score.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        className="cc-wv-card cc-wv-load"
        style={{ "--d": 1 } as CSSProperties}
      >
        <p className="cc-wv-card__head">{copy.loadTitle}</p>
        <ul>
          {copy.load.map((row, index) => (
            <li key={row.label} data-new={index === copy.load.length - 1}>
              <span>{row.label}</span>
              <i
                style={
                  {
                    "--w": `${(row.seconds / slowest) * 100}%`,
                  } as CSSProperties
                }
              />
              <b>{row.valueText}</b>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 3. more get in touch

function ContactView({ copy }: { copy: WebValueDemoCopy["contact"] }) {
  return (
    <CcPhotoScene photo={copy.photo} variant="contact">
      <div
        className="cc-wv-card cc-wv-mail"
        style={{ "--d": 0 } as CSSProperties}
      >
        <p className="cc-wv-card__head">
          <CcToolLogo name="gmail" />
          {copy.mail.app}
          <em>{copy.mail.time}</em>
        </p>
        <b>{copy.mail.title}</b>
        <small>{copy.mail.from}</small>
        <p>{copy.mail.text}</p>
        <span className="cc-wv-mail__source">{copy.mail.source}</span>
      </div>

      <div
        className="cc-wv-card cc-wv-booking"
        style={{ "--d": 1 } as CSSProperties}
      >
        <CcToolLogo name="google-calendar" size={26} />
        <span>
          <small>{copy.booking.app}</small>
          <b>{copy.booking.title}</b>
          <em>{copy.booking.when}</em>
        </span>
        <span className="cc-wv-booking__note">
          <CalendarCheck size={13} strokeWidth={2.2} />
          {copy.booking.note}
        </span>
      </div>
    </CcPhotoScene>
  );
}
